/**
 * Activity Service
 *
 * Handles API calls for activity feed.
 */

import api from './api';

export type ActivityType =
  | 'stream_upload'
  | 'stream_play'
  | 'follow'
  | 'favorite'
  | 'comment'
  | 'rating';

export interface Activity {
  userId: string;
  timestamp: number;
  type: ActivityType;
  targetUserId?: string;
  actorUsername: string;
  actorDisplayName?: string;
  actorProfilePicture?: string;
  streamId?: string;
  streamName?: string;
  streamCoverImage?: string;
  targetUsername?: string;
  metadata?: Record<string, unknown>;
  timeAgo: string;
}

export interface ActivitiesResponse {
  activities: Activity[];
  nextToken?: string;
}

export type FeedFilter = 'all' | 'streams' | 'social';

export const activityService = {
  /**
   * Get user's own activity
   */
  async getUserActivity(limit: number = 20, cursor?: string): Promise<ActivitiesResponse> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) {
      params.append('cursor', cursor);
    }
    return api.get<ActivitiesResponse>(`/activity?${params.toString()}`);
  },

  /**
   * Get activity feed from followed users
   */
  async getFeed(limit: number = 20, cursor?: string): Promise<ActivitiesResponse> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) {
      params.append('cursor', cursor);
    }
    return api.get<ActivitiesResponse>(`/feed?${params.toString()}`);
  },

  /**
   * Get combined feed (own + followed) with optional filter
   */
  async getCombinedFeed(
    limit: number = 20,
    filter: FeedFilter = 'all',
    cursor?: string
  ): Promise<ActivitiesResponse & { filter: FeedFilter }> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      filter
    });
    if (cursor) {
      params.append('cursor', cursor);
    }
    return api.get<ActivitiesResponse & { filter: FeedFilter }>(`/feed/combined?${params.toString()}`);
  }
};

export default activityService;
